import { cn } from '@/lib/utils'
import { mancalaLayout, seatStyle } from '../helpers'
import type { BoardProps } from './types'

/* ------------------------------------------------------------------ mancala */

interface MancalaView {
  pits: number[]
  turn: number
  last: number | null
  extra: number | null
}

function Seeds({ n }: { n: number }) {
  return (
    <span className="flex max-w-full flex-wrap items-center justify-center gap-0.5" aria-hidden>
      {Array.from({ length: Math.min(n, 12) }, (_, i) => (
        <span key={i} className="size-1.5 rounded-full bg-text-secondary" />
      ))}
    </span>
  )
}

export function Mancala({ view, seat, over, busy, send, players, people }: BoardProps<MancalaView>) {
  const v = seat ?? 0
  const L = mancalaLayout(v)
  const play = seat !== null && seat === view.turn && !over && !busy
  const name = (s: number) => people.get(players[s] ?? '')?.displayName.split(' ')[0] ?? 'Player'
  const store = (idx: number, owner: number) => (
    <div className={cn('grid w-16 shrink-0 place-items-center gap-1 rounded-2xl border-2 p-2 sm:w-20', seatStyle(owner).border, seatStyle(owner).soft)} aria-label={`${name(owner)}'s store: ${view.pits[idx]}`}>
      <span className="num text-2xl font-bold tabular-nums">{view.pits[idx]}</span>
      <Seeds n={view.pits[idx]!} />
    </div>
  )
  const pit = (idx: number, owner: number) => {
    const mine = owner === v
    const can = play && mine && view.pits[idx]! > 0
    return (
      <button
        key={idx}
        type="button"
        disabled={!can}
        onClick={() => send({ pit: idx % 7 })}
        aria-label={`${name(owner)}'s pit ${(idx % 7) + 1}: ${view.pits[idx]} seeds`}
        className={cn('grid aspect-square min-w-0 flex-1 place-items-center gap-0.5 rounded-full border border-border bg-card p-1 transition-colors', can && 'border-brand hover:bg-accent', view.last === idx && 'ring-2 ring-warning')}
      >
        <span className="num text-lg font-semibold tabular-nums">{view.pits[idx]}</span>
        <Seeds n={view.pits[idx]!} />
      </button>
    )
  }
  const other = 1 - v
  return (
    <div className="mx-auto grid w-full max-w-2xl gap-2">
      <p className="text-center text-xs text-text-muted">{name(other)} plays along the top, you along the bottom. Seeds travel anticlockwise.</p>
      <div className="flex items-stretch gap-2 rounded-3xl border border-border bg-warning-soft p-3">
        {store(L.theirStore, other)}
        <div className="grid min-w-0 flex-1 gap-2">
          <div className="flex gap-2">{L.theirs.map((i) => pit(i, other))}</div>
          <div className="flex gap-2">{L.mine.map((i) => pit(i, v))}</div>
        </div>
        {store(L.myStore, v)}
      </div>
      {view.extra !== null && !over ? <p className="text-center text-xs font-medium text-brand">{name(view.extra)} landed in their store and goes again.</p> : null}
    </div>
  )
}

/* ------------------------------------------------------------------ dots and boxes */

interface DotsView {
  h: (number | null)[]
  v: (number | null)[]
  boxes: (number | null)[]
  turn: number
  scores: number[]
  last: { kind: 'h' | 'v'; r: number; c: number } | null
}
const ROWS = 5
const COLS = 5
const GAP = 60
const PAD = 24

export function Dots({ view, seat, over, busy, send }: BoardProps<DotsView>) {
  const play = seat !== null && seat === view.turn && !over && !busy
  const W = COLS * GAP + PAD * 2
  const H = ROWS * GAP + PAD * 2
  const x = (c: number) => PAD + c * GAP
  const y = (r: number) => PAD + r * GAP
  const edge = (kind: 'h' | 'v', r: number, c: number) => {
    const drawn = kind === 'h' ? view.h[r * COLS + c] : view.v[r * (COLS + 1) + c]
    const [x1, y1, x2, y2] = kind === 'h' ? [x(c), y(r), x(c + 1), y(r)] : [x(c), y(r), x(c), y(r + 1)]
    const taken = drawn !== null && drawn !== undefined
    const fresh = view.last && view.last.kind === kind && view.last.r === r && view.last.c === c
    return (
      <g key={`${kind}${r}-${c}`}>
        <line x1={x1} y1={y1} x2={x2} y2={y2} strokeWidth={taken ? 7 : 4} strokeLinecap="round" className={cn(taken ? seatStyle(drawn).stroke : 'stroke-border', fresh && 'opacity-70')} />
        {!taken && play ? (
          <line
            x1={x1}
            y1={y1}
            x2={x2}
            y2={y2}
            strokeWidth={26}
            strokeLinecap="round"
            className="cursor-pointer stroke-transparent hover:stroke-brand/30"
            role="button"
            aria-label={`Draw the ${kind === 'h' ? 'horizontal' : 'vertical'} line at row ${r + 1}, column ${c + 1}`}
            tabIndex={0}
            onClick={() => send({ kind, r, c })}
            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && send({ kind, r, c })}
          />
        ) : null}
      </g>
    )
  }
  return (
    <div className="mx-auto w-full max-w-md">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full rounded-xl border border-border bg-card" role="group" aria-label="Dots and boxes board">
        {view.boxes.map((o, i) =>
          o === null ? null : (
            <rect key={i} x={x(i % COLS) + 4} y={y(Math.floor(i / COLS)) + 4} width={GAP - 8} height={GAP - 8} rx={8} className={seatStyle(o).softFill} />
          ),
        )}
        {Array.from({ length: (ROWS + 1) * COLS }, (_, i) => edge('h', Math.floor(i / COLS), i % COLS))}
        {Array.from({ length: ROWS * (COLS + 1) }, (_, i) => edge('v', Math.floor(i / (COLS + 1)), i % (COLS + 1)))}
        {Array.from({ length: (ROWS + 1) * (COLS + 1) }, (_, i) => (
          <circle key={i} cx={x(i % (COLS + 1))} cy={y(Math.floor(i / (COLS + 1)))} r={5.5} className="fill-foreground" />
        ))}
      </svg>
      <p className="mt-2 text-center text-xs text-text-muted">Close a box to score and go again.</p>
    </div>
  )
}
